import "./LeadsDetails.css";
import { TabComponent } from "@syncfusion/ej2-react-navigations";
import React, { useState, useEffect, Fragment } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getDownloadURL, getStorage, listAll, ref } from "firebase/storage";
import { db } from "../../firebase";
import NoDataFoundIcon from "../../assets/icons/no_data_found.svg";
import NoDataFound from "../../components/Common/NoDataFound/NoDataFound";

// Controllers Start
import GetLeadDealController from "../../controllers/Enquiries/GetLeadDealController";
import GetLeadProperties from "../../controllers/Enquiries/GetLeadProperties";
import GetLogVisitorsForLeads from "../../controllers/properties/GetLogVisitorsForLead";
import GetProperty from "../../controllers/properties/getProperty";
import GetPropertiesForLead from "../../controllers/leads/GetPropertiesForLead";
// Controllers End

// Common Components Start
import LeadPropertyContent from "../../components/User/Leads/LeadProperties/LeadPropertyContent";
import TableMenu from "../../components/Common/TableMenu/TableMenu";
import ContentLoader from "../../components/Common/ContentLoader/ContentLoader";
import DealsCard from "../../components/Deals/DealsCard/DealsCard";
import ConfirmationModal from "../../components/Common/ConfirmationModal/ConfirmationModal";
import DeleteLead from "../../controllers/leads/DeleteLead";

// Common Components End

// Form Validate imports Start
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
// Form Validate imports End

// MUI Start
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import getSettings from "../../controllers/settings/getSettings";
import UpdateSingleLead from "../../controllers/leads/UpdateSingleLead";
import isDuplicateCNIC from "../../controllers/duplicates/isDuplicateCNIC";
import { ToastSuccessMessages } from "../../controllers/auth/AuthErrorMessages";
import { ToastContainer } from "react-toastify";
import isDuplicatePhoneNumber from "../../controllers/duplicates/isDuplicatePhoneNumber";
import GetSingleLead from "../../controllers/leads/GetSingleLead";
import LeadForm from "../../components/LeadForm/LeadForm";
import { doc, onSnapshot } from "firebase/firestore";
import { stringAvatar } from "../../helpers/stringAvatar";
import { Avatar } from "@mui/material";

import noDataWebIcon from "../../assets/icons/new-icons/noDataFoundWeb.svg";
import UserListCardDetail from "../../components/UserListCardDetail/UserListCardDetail";
import PropertiesContent from "../Properties/PropertiesContent/PropertiesContent";
import SmallScreensLogVisitsComponent from "../../components/Common/SmallScreensLogVisitsComponent/SmallScreensLogVisitsComponent";
// MUI End

let ID = "";

// Form Validate schema Start
const schema = yup.object().shape({
  name: yup
    .string()
    .min(3, "Name must have atleast 3 characters")
    .required("Required"),
  phone: yup
    .string()
    .length(11, "Invalid phone number")
    .required("Required")
    .default("03"),
  email: yup.string().email("Invalid email!"),
  company: yup.string(),
  cnic: yup.string().max(15).default(""),
  address: yup.string(),
});
// Form Validate schema End

const LeadsDetails = () => {
  let location = useLocation();
  const clientURLId = location.pathname.split("/").pop();

  /** Deals */
  const [Deals, setDeals] = useState([]);
  const [RequestingDeals, setRequestingDeals] = useState(true);

  // useStates Start
  const [leadsDetails, setLeadsDetails] = useState([]);
  const [leadProperties, setLeadProperties] = useState([]);
  const [leadVisits, setLeadVisits] = useState(null);
  const [RequestingVisits, setRequestingVisits] = useState(true);
  const [RequestingPropertiers, setRequestingPropertiers] = useState(true);
  // useStates End

  // Modal Start
  const [open, setOpen] = React.useState(false);
  const handleOpen = async () => {
    setOpen("update");

    setValue("name", leadsDetails.name);
    setValue("phone", leadsDetails.phone);
    setValue("email", leadsDetails.email);
    setValue("company", leadsDetails.company);
    setValue("cnic", leadsDetails.cnic);
    setValue("address", leadsDetails.address);
  };
  const handleClose = () => {
    setValue("name", "");
    setValue("phone", "");
    setValue("email", "");
    setValue("company", "");
    setValue("cnic", "");
    setValue("address", "");
    setError("cnic", {
      message: "",
    });
    setError("phone", {
      message: "",
    });
    setOpen(false);
  };
  // Modal End

  const [isDisabled, setIsDisabled] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [RefreshPage, setRefreshPage] = useState(false);
  const RefreshIt = () => {
    setLeadsDetails(watch());
    setRefreshPage(true);
  };

  const submitForm = async (e) => {
    setIsDisabled(true);
    //getting the orignial lead data to check for new data
    const singleLead = await GetSingleLead(ID);

    //checking for duplicates
    if (e.phone !== singleLead.phone) {
      if (e.phone !== "") {
        if (await isDuplicatePhoneNumber(e.phone)) {
          setError("phone", {
            message:
              "this phone number already exists! use a different phone number ",
          });
          setIsDisabled(false);
          return;
        }
      }
    }
    if (e.cnic !== singleLead.cnic) {
      if (e.cnic !== "") {
        if (await isDuplicateCNIC(e.cnic)) {
          setError("cnic", {
            message:
              "this cnic number already exists! use a different cnic number ",
          });
          setIsDisabled(false);
          return;
        }
      }
    }

    const leads = await UpdateSingleLead(e, ID);
    if (leads !== null) {
      reset();
      setIsDisabled(false);
      ToastSuccessMessages("lead/updating-successful");
      RefreshIt();
    } else {
      setValue("cnic", "");
      setError("cnic", {
        type: "required",
        message: "Duplicate record found! Can't create new lead",
      });
      setIsDisabled(false);
      return;
    }
    RefreshIt();
    handleClose();
  };

  // side effect for listening to real time updates
  useEffect(() => {
    let leadsID = location.pathname.split("/").pop();

    let lead = {};
    let unsubscribe = onSnapshot(doc(db, "leads", leadsID), (doc) => {
      lead = { id: doc.id, ...doc.data() };
      setLeadsDetails(lead);
    });

    return () => {
      unsubscribe();
    };
  }, [clientURLId]);

  // Lead Deal useEffect Start
  useEffect(() => {
    async function fetchData() {
      setRequestingPropertiers(true);

      const leadId = location.pathname.split("/").pop();

      let propertiesForLead = await GetPropertiesForLead(leadId);

      let leadPropertiesIds = [];

      for (let index = 0; index < propertiesForLead.length; index++) {
        const { id } = propertiesForLead[index];
        const { items } = await listAll(ref(getStorage(), `/properties/${id}`));
        let thumbnail;
        if (items.length !== 0) thumbnail = await getDownloadURL(items[0]);
        else
          thumbnail =
            "https://firebasestorage.googleapis.com/v0/b/property-management-port-12157.appspot.com/o/general_images%2Fproperty-avatar.jpg?alt=media&token=76422344-c1de-4415-a036-b7e62e8c06de";
        propertiesForLead[index] = {
          ...propertiesForLead[index],
          thumbnail,
        };
        setRequestingPropertiers(false);
      }

      let getLeadDealProperties = [];

      const leadDeal = await GetLeadDealController(leadId);

      if (leadDeal.length === 0) {
        getLeadDealProperties = [];
      } else {
        leadPropertiesIds = propertiesForLead.map((leadProperty) => {
          return leadProperty.id;
        });

        let leadDealAfterRemoveDuplicates = leadDeal.filter(
          (deal) => !leadPropertiesIds.includes(deal.property)
        );

        let leadDealPropertyIds = leadDealAfterRemoveDuplicates.map((deal) => {
          return deal.property;
        });

        getLeadDealProperties =
          (await GetLeadProperties(leadDealPropertyIds)) || [];

        for (let index = 0; index < getLeadDealProperties.length; index++) {
          const { id } = getLeadDealProperties[index];
          const { items } = await listAll(
            ref(getStorage(), `/properties/${id}`)
          );

          let thumbnail;
          if (items.length !== 0) thumbnail = await getDownloadURL(items[0]);
          else
            thumbnail =
              "https://firebasestorage.googleapis.com/v0/b/property-management-port-12157.appspot.com/o/general_images%2Fproperty-avatar.jpg?alt=media&token=76422344-c1de-4415-a036-b7e62e8c06de";
          getLeadDealProperties[index] = {
            ...getLeadDealProperties[index],
            thumbnail,
          };
          setRequestingPropertiers(false);
        }
      }

      setLeadProperties([...getLeadDealProperties, ...propertiesForLead]);
      setRequestingPropertiers(false);
    }
    fetchData();
  }, [location.pathname, RefreshPage, clientURLId]);
  // Lead Deal useEffect End

  // Lead Visits useEffect Start
  useEffect(() => {
    async function fetchData() {
      setRequestingVisits(true);
      const leadId = location.pathname.split("/").pop();

      let leadVisitsData = await GetLogVisitorsForLeads(leadId);
      console.log("leadVisitsData", leadVisitsData);
      function extractLeads(arr, prop) {
        let extractedValue = arr.map((item) => item[prop]);
        return extractedValue;
      }
      // propertyId in visitors object
      let propertyID = Array.from(
        new Set(extractLeads(leadVisitsData, "propertyId"))
      ); // propertyId:xyz

      // fetch single property data / property object
      let propertyData = [];
      for (var i = 0; i < propertyID.length; i++) {
        propertyData.push(await GetProperty(propertyID[i]));
      }

      // replace property id with name from property data / property object
      leadVisitsData = leadVisitsData.map((visit) => {
        return {
          ...visit,
          propertyId: propertyData.filter(
            (property) => property.id === visit.propertyId
          )[0].propertyTitle,
        };
      });

      //  propertyData =await GetProperty(propertyId);

      setLeadVisits(leadVisitsData);
      setRequestingVisits(false);
    }
    fetchData();
  }, [location.pathname, RefreshPage, clientURLId]);
  // Lead Visits useEffect End

  useEffect(() => {
    async function fetchData() {
      setRequestingDeals(true);
      const leadId = location.pathname.split("/").pop();

      /** Get all lead deals */
      const deals = await GetLeadDealController(leadId);
      setDeals(deals);

      setRequestingDeals(false);
    }
    fetchData();
  }, [location.pathname, RefreshPage, clientURLId]);

  // Currency Settings UseEffect Start
  const [CurrencySettings, setCurrencySettings] = useState({});
  useEffect(() => {
    async function fetchData() {
      const settings = await getSettings();
      setCurrencySettings(settings);
    }
    fetchData();
  }, [clientURLId]);
  // Currency Settings UseEffect End
  const navigate = useNavigate();

  //  agent details active button

  const [agentDetailBtnStatus, setAgentDetailBtnStatus] = useState("deals");

  const [DeleteDialog, setDeleteDialog] = useState(false);
  const [DeleteLeadId, setDeleteLeadId] = useState("");
  const handleOpenDeleteDialog = (id) => {
    setDeleteLeadId(id);
    setDeleteDialog(true);
  };

  const handleCloseDeleteDialog = async (del) => {
    setDeleteDialog(false);
    if (del === true) {
      await DeleteLead(DeleteLeadId);

      setDeleteDialog(false);
      navigate("/user/clients");
    }
  };

  //Check that is it mobile screen
  const [isMobile, setIsMobile] = useState(false);
  const setWindowDimensions = () => {
    if (window.innerWidth < 767) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", setWindowDimensions);
    setWindowDimensions();
    return () => {
      window.removeEventListener("resize", setWindowDimensions);
    };
  }, []);

  return (
    <Fragment>
      <div className="global-body-container client-details-page">
        <div className="d-flex align-items-start">
          <h2 className="custom-heading">Client Details</h2>
        </div>
        <div className="client-details-bar">
          <div className="client-details">
            <div className="client-details-image ">
              <Avatar {...stringAvatar(leadsDetails?.name)} />
            </div>
            <div className="client-details-name-added">
              <h2
                className="client-details-name "
                style={{
                  fontWeight: 500,
                }}
              >
                {leadsDetails?.name}
              </h2>
              <span className="client-details-added">
                Added: {leadsDetails?.createdAt?.toDate().toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="edit-delete-svg">
            <a
              onClick={() => {
                handleOpenDeleteDialog(leadsDetails?.id);
              }}
            >
              <svg
                width="35"
                height="35"
                viewBox="0 0 42 42"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="42" height="42" rx="10" fill="#EBEBEB" />
                <path
                  d="M18.7105 12.75L15.543 15.9262M24.293 12.75L27.4605 15.9262"
                  stroke="#FF0000"
                  strokeOpacity="0.7"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.75 17.8672C12.75 16.2484 13.6162 16.1172 14.6925 16.1172H28.3075C29.3838 16.1172 30.25 16.2484 30.25 17.8672C30.25 19.7484 29.3838 19.6172 28.3075 19.6172H14.6925C13.6162 19.6172 12.75 19.7484 12.75 17.8672Z"
                  stroke="#FF0000"
                  strokeOpacity="0.7"
                  strokeWidth="1.5"
                />
                <path
                  d="M19.54 23.25V26.3562M23.565 23.25V26.3562M14.0625 19.75L15.2962 27.31C15.5762 29.0075 16.25 30.25 18.7525 30.25H24.0288C26.75 30.25 27.1525 29.06 27.4675 27.415L28.9375 19.75"
                  stroke="#FF0000"
                  strokeOpacity="0.7"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </a>

            <a onClick={handleOpen}>
              <svg
                style={{ marginLeft: "5px" }}
                width="35"
                height="35"
                viewBox="0 0 42 42"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="42" height="42" rx="10" fill="#EBEBEB" />
                <path
                  opacity="0.8"
                  d="M12.8193 29.4326L12.8174 29.4307C12.7736 29.3871 12.75 29.344 12.75 29.25V26.425C12.75 26.388 12.7564 26.3538 12.7711 26.3172L12.7716 26.3159C12.7821 26.2896 12.7988 26.2619 12.8303 26.2303L22.6 16.4607L25.7893 19.65L16.0197 29.4197C15.9878 29.4516 15.96 29.4683 15.9345 29.4786C15.8967 29.4936 15.8618 29.5 15.825 29.5H13C12.906 29.5 12.8629 29.4764 12.8193 29.4326ZM28.2969 17.1175L25.1138 13.9719L25.9803 13.1053C26.2183 12.8674 26.4965 12.75 26.863 12.75C27.2286 12.75 27.5065 12.8672 27.7447 13.1053L29.1447 14.5053C29.393 14.7536 29.5146 15.0348 29.5254 15.3861C29.5356 15.7204 29.4318 15.9825 29.1947 16.2197L28.2969 17.1175Z"
                  stroke="black"
                  strokeOpacity="0.8"
                  strokeWidth="1.5"
                />
              </svg>
            </a>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12 mb-4">
            <ul
              className="client-details-contact"
              style={{
                fontWeight: 500,
              }}
            >
              {/* phone number  */}
              {leadsDetails?.phone && (
                <li>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="10" cy="10" r="10" fill="#4C3CA6" />
                    <path
                      d="M6.22849 9.24169C7.23712 11.4385 9.03187 13.185 11.2632 14.1319L11.6209 14.2913C12.4392 14.6559 13.4012 14.3789 13.9003 13.6349L14.3677 12.9383C14.5197 12.7117 14.4734 12.4066 14.2611 12.2354L12.6759 10.9565C12.4431 10.7687 12.1007 10.8125 11.9226 11.0528L11.4322 11.7145C10.1739 11.0938 9.15229 10.0722 8.53159 8.81387L9.19329 8.32349C9.43366 8.14538 9.4774 7.803 9.28956 7.57017L8.01069 5.98494C7.83945 5.77268 7.53445 5.72638 7.30794 5.87826L6.60645 6.34863C5.85785 6.85058 5.58238 7.8205 5.9554 8.641L6.22808 9.2408L6.22849 9.24169Z"
                      fill="white"
                    />
                  </svg>

                  <span className="client-details-contact-details">
                    {leadsDetails?.phone}
                  </span>
                </li>
              )}

              {/* end phone number  */}
              {/* email  */}
              {leadsDetails?.email && (
                <li>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="10" cy="10" r="10" fill="#4C3CA6" />
                    <path
                      d="M5.54988 8.0304C5.46478 7.98092 5.35621 8.03417 5.34629 8.13094C5.20786 9.48162 5.24136 10.8451 5.4468 12.1895C5.55511 12.8983 6.14294 13.4417 6.86854 13.5038L7.69251 13.5743C9.40273 13.7206 11.1226 13.7206 12.8329 13.5743L13.6569 13.5038C14.3824 13.4417 14.9703 12.8983 15.0786 12.1895C15.29 10.8059 15.3193 9.40211 15.1666 8.0129C15.1558 7.91544 15.0454 7.86317 14.9607 7.91476L11.8811 9.79088C10.8891 10.3952 9.63831 10.4078 8.63389 9.8238L5.54988 8.0304Z"
                      fill="white"
                    />
                    <path
                      d="M7.83735 5.35994C9.46705 5.23158 11.106 5.23158 12.7357 5.35994L13.5209 5.42179C14.0436 5.46296 14.4913 5.74545 14.7232 6.14954C14.7529 6.20123 14.7306 6.26382 14.676 6.29449L11.4183 8.1218C10.7196 8.51369 9.83858 8.52189 9.13115 8.14314L5.85168 6.38724C5.7987 6.35887 5.77438 6.30048 5.79826 6.24908C6.01046 5.79234 6.48754 5.46626 7.05217 5.42179L7.83735 5.35994Z"
                      fill="white"
                    />
                  </svg>
                  <span className="client-details-contact-details">
                    {leadsDetails?.email}
                  </span>
                </li>
              )}

              {/* end email  */}
              {leadsDetails?.address && (
                <li>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="10" cy="10" r="10" fill="#4C3CA6" />
                    <path
                      d="M11.4432 3.65884C10.7969 2.99133 9.72631 2.99133 9.07999 3.65883L5.92312 6.91919C5.74181 7.10643 5.61999 7.34315 5.57297 7.5995C5.18958 9.69029 5.16127 11.8307 5.48926 13.9309L5.60538 14.6744C5.64206 14.9093 5.84433 15.0825 6.08204 15.0825H8.28793C8.4696 15.0825 8.61688 14.9352 8.61688 14.7535V10.1483H11.9064V14.7535C11.9064 14.9352 12.0536 15.0825 12.2353 15.0825H14.4412C14.6789 15.0825 14.8812 14.9093 14.9178 14.6744L15.0339 13.9309C15.3619 11.8307 15.3336 9.69029 14.9503 7.5995C14.9032 7.34315 14.7814 7.10643 14.6001 6.91919L11.4432 3.65884Z"
                      fill="white"
                    />
                  </svg>

                  <span className="client-details-contact-details">
                    {leadsDetails?.address}
                  </span>
                </li>
              )}
            </ul>
            {isMobile && (
              <UserListCardDetail
                name={leadsDetails?.name}
                date={leadsDetails?.createdAt
                  ?.toDate()
                  ?.toLocaleString("en-GB", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                phone={leadsDetails?.phone}
                email={leadsDetails?.email}
                cnic={leadsDetails?.cnic}
                address={leadsDetails?.address}
                onEdit={() => {
                  handleOpen("update", leadsDetails.id);
                }}
                // onClick={() =>
                //   navigate(`/user/leads/details/${lead.id}`)
                // }
                onDelete={() => {
                  handleOpenDeleteDialog(leadsDetails.id);
                }}
              />
            )}
            <div className="client-details-buttons">
              <button
                type="button"
                className={`  ${
                  agentDetailBtnStatus === "deals"
                    ? " client-detail-button-active"
                    : " client-detail-button"
                } `}
                onClick={() => {
                  setAgentDetailBtnStatus("deals");
                }}
              >
                Deals
              </button>
              <button
                type="button"
                className={`  ${
                  agentDetailBtnStatus === "Property Listing"
                    ? " client-detail-button-active"
                    : " client-detail-button"
                } `}
                onClick={() => {
                  setAgentDetailBtnStatus("Property Listing");
                }}
              >
                Property Listing
              </button>
              <button
                type="button"
                className={` mr-2  ${
                  agentDetailBtnStatus === "Visits"
                    ? " client-detail-button-active"
                    : " client-detail-button"
                } `}
                onClick={() => {
                  setAgentDetailBtnStatus("Visits");
                }}
              >
                Visits
              </button>
            </div>

            <div className="client-details-box">
              {
                //  Show deals
                agentDetailBtnStatus === "deals" ? (
                  <>
                    <div className="client-deals-container">
                      {Deals.length !== 0 &&
                        Deals.map((dls, index) => {
                          const {
                            agent,
                            comments,
                            currentstatus,
                            interest,
                            client,
                            property,
                            createdAt,
                            transaction,
                            id,
                            dealPrice,
                          } = dls;
                          return (
                            <Link
                              key={index}
                              to={`/user/deals/update/${id}`}
                              className="client-deals-card-link"
                            >
                              <DealsCard
                                {...{
                                  agent: agent.id,
                                  comments: comments,
                                  status: currentstatus,
                                  interest: interest,
                                  lead: client.id,
                                  property: property.id,
                                  createdAt: createdAt,
                                  transaction: transaction,
                                  dealPrice: dealPrice,
                                  thumbnail: property.images
                                    ? property.images[0]
                                    : "https://firebasestorage.googleapis.com/v0/b/property-management-port-12157.appspot.com/o/general%2FloadingImg.gif?alt=media&token=5b6fb56c-aa61-4657-8f48-3a97b507f9c5",
                                  CurrencySettings: CurrencySettings,
                                }}
                              />
                            </Link>
                          );
                        })}
                      {!isMobile && Deals.length === 0 ? (
                        <div className="client-deals-no-data-found">
                          <NoDataFound />
                        </div>
                      ) : isMobile && Deals.length === 0 ? (
                        <div className="mobile-client-deals-no-data-found">
                          <NoDataFound />
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </>
                ) : agentDetailBtnStatus === "Property Listing" ? (
                  <div className="row client-properties-container">
                    {!RequestingPropertiers &&
                      leadProperties &&
                      leadProperties.map((property, index) => {
                        return <PropertiesContent property={property} />;
                      })}
                    {RequestingPropertiers && <ContentLoader />}
                    {!RequestingPropertiers &&
                    !isMobile &&
                    leadProperties.length === 0 ? (
                      <div className="client-properties-no-data-found">
                        <NoDataFound />
                      </div>
                    ) : !RequestingPropertiers &&
                      isMobile &&
                      leadProperties.length === 0 ? (
                      <div className="mobile-client-properties-no-data-found">
                        <NoDataFound />
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                ) : (
                  agentDetailBtnStatus === "Visits" && (
                    <>
                      {!RequestingVisits && leadVisits?.length !== 0 && (
                        <div className="client-visits-container">
                          {/* <div className="col-12"> */}
                          {!RequestingVisits &&
                            leadVisits?.map((visit) => {
                              return (
                                <SmallScreensLogVisitsComponent
                                  ClientName={visit.client.name}
                                  Status={visit.status}
                                  Description={visit.comment}
                                  DateTime={visit.visitTime}
                                  DealID={visit.dealId}
                                />
                              );
                            })}
                          {/* </div> */}
                        </div>
                      )}
                      {!isMobile && leadVisits?.length === 0 ? (
                        <div className="client-visits-no-data-found">
                          <NoDataFound />
                        </div>
                      ) : isMobile && leadVisits?.length === 0 ? (
                        <div className="mobile-client-visits-no-data-found">
                          <NoDataFound />
                        </div>
                      ) : (
                        ""
                      )}
                    </>
                  )
                )
              }
            </div>
          </div>
        </div>
      </div>
      <LeadForm
        {...{
          open,
          setOpen,
          SingleLeadData: leadsDetails,
          LeadId: location.pathname.split("/").pop(),
          handleRefresh: () => {},
        }}
      />
      <ConfirmationModal
        title={"Delete Confirmation"}
        description={"Are you sure you want to delete this client?"}
        openDialog={DeleteDialog}
        handleCloseDialog={handleCloseDeleteDialog}
      />
      <ToastContainer />
    </Fragment>
  );
};

export default LeadsDetails;
